import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocalStorage } from "./useLocalStorage.hook";
import { User } from "../interfaces";
import { api } from "../config/apiCaller.config";
import { AxiosResponse } from "axios";

interface AuthContextType {
    user: User | undefined;
    loading: boolean;
    login: (username: string, password: string) => Promise<AxiosResponse<any>>;
    logout: () => void;
    token?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: {children: any}) => {
    const [token, setToken] = useLocalStorage('session', undefined);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<User>();

    useEffect(() => {
        if (token) {
            loadUser();
        } else {
            setUser(undefined);
            setLoading(false);
        }
    }, [token]);

    const login = async (username: string, password: string) => {
        try {
            setLoading(true);
            const response = await api.post('/auth/signin', { username, password });
            setToken(response.data.accessToken);

            return response;
        } catch (error: any) {
            return error.response
        }
    }

    const logout = () => {
        setToken(undefined);
        setUser(undefined);
        setLoading(false);
    }

    const loadUser = async () => {
        // fetch user data
        try {
            const response = await api.get('/customers/me');
            if (response.status === 200) {
                setUser(response.data);
            } else if (response.status === 401) {
                logout();
            }
        } catch (error) {
            logout();
        } finally {
            setLoading(false);
        }
    }

    const value = useMemo(
        () => ({
            user,
            loading,
            login,
            logout,
            token
        }),
        [user, loading]
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}